import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { Bars } from "react-loader-spinner";
import axiosInstance from "../../Shared/axiosInstance.js"; // ✅ استخدم نفس الـ instance

export default function UpdateExpense() {
  const [isLoadingData, setIsLoadingData] = useState(true); // حالة تحميل البيانات
  const [isLoading, setIsLoading] = useState(false); // حالة إرسال النموذج
  const [errorMsg, setErrorMsg] = useState(null); // رسالة الخطأ

  const navigate = useNavigate();
  const { id } = useParams(); // جلب الـ id من رابط الصفحة

  // ✅ جلب مصروف معين بالـ id
  async function getExpenseById(expenseId) {
    try {
      const { data } = await axiosInstance.get(`/Expense/expenses/${expenseId}`);
      return data?.data || null;
    } catch (error) {
      Swal.fire({
        title: "خطأ!",
        text: "فشل تحميل بيانات المصروف.",
        icon: "error",
      });
      navigate("/expense");
      return null;
    }
  }

  // ✅ تحويل القيمة إلى رقم آمن
  const toSafeNumber = (value, defaultValue = 0) => {
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  // ✅ معالجة التغييرات في الـ select
  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    formik.setFieldValue(name, toSafeNumber(value, 0));
  };

  // ✅ إعدادات Formik
  const formik = useFormik({
    initialValues: {
      id: "",
      type: 0,
      amount: "",
      date: "",
      method: 0,
      notes: "",
    },
    validate: (values) => {
      const errors = {};
      setErrorMsg(null);
      if (!values.amount || values.amount <= 0) errors.amount = "المبلغ يجب أن يكون أكبر من 0";
      if (!values.date) errors.date = "تاريخ المصروف مطلوب";
      return errors;
    },
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        const body = {
          id: values.id,
          type: toSafeNumber(values.type, 0),
          amount: parseFloat(values.amount) || 0,
          date: values.date,
          method: toSafeNumber(values.method, 0),
          notes: values.notes || "",
        };

        const { data } = await axiosInstance.put(`/Expense/expenses/${id}`, body);

        if (data?.status === 200) {
          await Swal.fire({
            title: "تم التحديث!",
            text: data.message || "تم تحديث المصروف بنجاح",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
          navigate("/expense");
        }
      } catch (error) {
        setErrorMsg(
          error?.response?.data?.errors
            ? Object.values(error.response.data.errors).flat().join(", ")
            : error?.response?.data?.message || "حدث خطأ ما"
        );
      } finally {
        setIsLoading(false);
      }
    },
  });

  // ✅ تحميل البيانات عند فتح الصفحة
  useEffect(() => {
    async function load() {
      setIsLoadingData(true);
      const expense = await getExpenseById(id);

      if (expense) {
        const typeValue = expense.typeEnum ?? expense.type;
        const methodValue = expense.methodEnum ?? expense.method;

        formik.setValues({
          id: expense.id || "",
          type: toSafeNumber(typeValue, 0),
          amount: expense.amount || "",
          date: expense.date?.slice(0, 16) || "",
          method: toSafeNumber(methodValue, 0),
          notes: expense.notes || "",
        });
      }

      setIsLoadingData(false);
    }

    if (id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (isLoadingData) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
        <Bars height="80" width="80" color="#06b6d4" ariaLabel="loading" />
        <p style={{ marginTop: "20px", color: "#64748b", fontSize: "18px" }}>جاري تحميل بيانات المصروف...</p>
      </div>
    );
  }

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
      <div className="container py-5" style={{ maxWidth: "700px" }}>
        {/* رأس الصفحة */}
        <div className="d-flex justify-content-between align-items-center mb-4 mt-5 pt-5">
          <div>
            <h2 style={{ color: "#06b6d4", fontWeight: "700", fontSize: "28px", marginBottom: "4px" }}>
              <i className="bi bi-cash-coin me-2"></i>تحديث المصروف
            </h2>
            <small style={{ color: "#64748b", fontSize: "14px" }}>تعديل بيانات المصروف</small>
          </div>
          <button
            type="button"
            onClick={() => navigate("/expense")}
            style={{
              background: "#64748b",
              color: "#fff",
              padding: "10px 20px",
              borderRadius: "6px",
              border: "none",
              fontWeight: "600",
              fontSize: "15px",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => (e.target.style.background = "#475569")}
            onMouseLeave={(e) => (e.target.style.background = "#64748b")}
          >
            <i className="bi bi-arrow-left me-1"></i> رجوع
          </button>
        </div>

        {/* رسالة الخطأ */}
        {errorMsg && (
          <div style={{ background: "#fee2e2", border: "1px solid #fecaca", color: "#991b1b", padding: "12px 16px", borderRadius: "8px", marginBottom: "20px", fontSize: "14px", fontWeight: "600" }}>
            <i className="bi bi-exclamation-triangle me-2"></i>{errorMsg}
          </div>
        )}

        {/* نموذج التحديث */}
        <div style={cardStyle}>
          <form onSubmit={formik.handleSubmit}>
            {/* المبلغ */}
            <div className="mb-4">
              <label style={labelStyle}><i className="bi bi-cash-stack me-2"></i>المبلغ *</label>
              <input
                type="number"
                name="amount"
                placeholder="أدخل المبلغ"
                value={formik.values.amount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                style={{ ...inputStyle, borderColor: formik.touched.amount && formik.errors.amount ? "#dc2626" : "#cbd5e1" }}
              />
              {formik.errors.amount && formik.touched.amount && <div style={errorStyle}>{formik.errors.amount}</div>}
            </div>

            {/* التاريخ */}
            <div className="mb-4">
              <label style={labelStyle}><i className="bi bi-calendar-event me-2"></i>التاريخ *</label>
              <input
                type="datetime-local"
                name="date"
                value={formik.values.date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                style={{ ...inputStyle, borderColor: formik.touched.date && formik.errors.date ? "#dc2626" : "#cbd5e1" }}
              />
              {formik.errors.date && formik.touched.date && <div style={errorStyle}>{formik.errors.date}</div>}
            </div>

            {/* نوع المصروف */}
            <div className="mb-4">
              <label style={labelStyle}>النوع</label>
              <select
                name="type"
                value={formik.values.type}
                onChange={handleSelectChange}
                onBlur={formik.handleBlur}
                style={inputStyle}
              >
                <option value={0}>راتب</option>
                <option value={1}>صيانة سيارة</option>
                <option value={2}>إيجار سيارة</option>
                <option value={3}>إيجار مستودع</option>
                <option value={4}>أخرى</option>
              </select>
            </div>

            {/* طريقة الدفع */}
            <div className="mb-4">
              <label style={labelStyle}>طريقة الدفع</label>
              <select
                name="method"
                value={formik.values.method}
                onChange={handleSelectChange}
                onBlur={formik.handleBlur}
                style={inputStyle}
              >
                <option value={0}>كاش</option>
                <option value={1}>فودافون كاش</option>
                <option value={2}>إنستا باي</option>
              </select>
            </div>

            {/* الملاحظات */}
            <div className="mb-4">
              <label style={labelStyle}>ملاحظات</label>
              <textarea
                name="notes"
                placeholder="ملاحظات اختيارية"
                rows="4"
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                style={{ ...inputStyle, resize: "vertical", minHeight: "80px" }}
              />
            </div>

            {/* أزرار الحفظ والإلغاء */}
            <div className="d-flex gap-3">
              <button
                type="submit"
                disabled={!(formik.dirty && formik.isValid) || isLoading}
                style={{
                  flex: 1,
                  background: formik.dirty && formik.isValid && !isLoading ? "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)" : "#94a3b8",
                  color: "#fff",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "none",
                  fontWeight: "700",
                  fontSize: "18px",
                  cursor: formik.dirty && formik.isValid && !isLoading ? "pointer" : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                {isLoading ? <Bars height="25" width="25" color="#fff" /> : "تحديث المصروف"}
              </button>

              <button
                type="button"
                onClick={() => navigate("/expense")}
                style={{
                  flex: 1,
                  background: "#fff",
                  color: "#475569",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "2px solid #cbd5e1",
                  fontWeight: "700",
                  fontSize: "18px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                إلغاء
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ✅ ستايلات
const cardStyle = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  padding: "32px",
  marginBottom: "20px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
};

const labelStyle = {
  color: "#475569",
  fontSize: "15px",
  fontWeight: "600",
  marginBottom: "8px",
  display: "block",
};

const inputStyle = {
  width: "100%",
  padding: "12px 16px",
  border: "2px solid #cbd5e1",
  borderRadius: "8px",
  fontSize: "15px",
  color: "#1e293b",
  outline: "none",
  transition: "all 0.2s",
  fontWeight: "500",
};

const errorStyle = {
  color: "#dc2626",
  fontSize: "13px",
  marginTop: "6px",
  fontWeight: "600",
};
